import React, { useEffect, useState } from "react";
import { X, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fetchFileObjectUrl } from "@/lib/api";

const INIT = { open: false, url: "", name: "", loading: false, error: "", ct: "" };

// Global in-app document previewer. Listens for the "assk:preview-file" event
// (dispatched by api.openFileById) and shows the file inline in a modal so
// applicant documents are previewed, never downloaded.
export default function FilePreviewHost() {
  const [state, setState] = useState(INIT);

  useEffect(() => {
    const onPreview = async (e) => {
      const { fileId, filename } = e.detail || {};
      setState({ ...INIT, open: true, name: filename || "दस्तऐवज", loading: true });
      try {
        const { url, contentType } = await fetchFileObjectUrl(fileId);
        setState((s) => (s.open ? { ...s, url, ct: contentType, loading: false } : s));
      } catch (err) {
        setState((s) => ({ ...s, loading: false, error: "फाईल उघडता आली नाही." }));
      }
    };
    window.addEventListener("assk:preview-file", onPreview);
    return () => window.removeEventListener("assk:preview-file", onPreview);
  }, []);

  const close = () => {
    setState((s) => {
      if (s.url) setTimeout(() => URL.revokeObjectURL(s.url), 1000);
      return { ...s, open: false };
    });
  };

  const isImage = state.ct.startsWith("image/");

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="flex h-[95vh] w-[96vw] max-w-5xl flex-col gap-0 overflow-hidden p-0" data-testid="file-preview-modal">
        <div className="flex items-center gap-2.5 border-b-2 border-[#f1ead7] bg-[#fbf9f2] px-5 py-3">
          <DialogTitle className="flex-1 truncate text-[15px] font-semibold text-[#1b5e3f]" data-testid="file-preview-title">
            {state.name}
          </DialogTitle>
          <DialogDescription className="sr-only">अपलोड केलेल्या दस्तऐवजाचे पूर्वावलोकन</DialogDescription>
          {state.url && (
            <a
              href={state.url}
              target="_blank"
              rel="noreferrer"
              data-testid="file-preview-newtab"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#1b5e3f]/25 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-[#1b5e3f] hover:bg-[#f6f2e6]"
            >
              <ExternalLink className="h-3.5 w-3.5" /> नवीन टॅब
            </a>
          )}
          <button onClick={close} data-testid="file-preview-close" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 bg-slate-100">
          {state.loading && (
            <div className="flex h-full items-center justify-center text-[14px] text-slate-500">लोड होत आहे…</div>
          )}
          {state.error && (
            <div className="flex h-full items-center justify-center text-[14px] text-red-600" data-testid="file-preview-error">{state.error}</div>
          )}
          {!state.loading && !state.error && state.url && (
            isImage ? (
              <div className="flex h-full w-full items-center justify-center overflow-auto p-3">
                <img src={state.url} alt={state.name} className="max-h-full max-w-full object-contain" />
              </div>
            ) : (
              <iframe title={state.name} src={state.url} className="h-full w-full border-0" />
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
